import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import CTASection from '../components/CTASection';
import { CheckCircle2, ArrowRight, Headphones, Ticket, MessageSquare, BarChart2, Clock, Users } from 'lucide-react';

const ZohoDesk = () => {
  const features = [
    {
      icon: <Ticket className="w-6 h-6 text-blue-700" />,
      title: 'Ticketbeheer',
      description: 'Centraliseer alle klantvragen in één overzichtelijk systeem.',
    },
    {
      icon: <MessageSquare className="w-6 h-6 text-blue-700" />,
      title: 'Multi-channel Support',
      description: 'Email, chat, telefoon en social media in één platform.',
    },
    {
      icon: <BarChart2 className="w-6 h-6 text-blue-700" />,
      title: 'Rapportages',
      description: 'Inzicht in responstijden, klanttevredenheid en team performance.',
    },
    {
      icon: <Clock className="w-6 h-6 text-blue-700" />,
      title: 'SLA Management',
      description: 'Automatische escalaties en waarschuwingen voor SLA overtredingen.',
    },
    {
      icon: <Users className="w-6 h-6 text-blue-700" />,
      title: 'Team Samenwerking',
      description: 'Efficiënte samenwerking binnen uw support team.',
    },
    {
      icon: <Headphones className="w-6 h-6 text-blue-700" />,
      title: 'Kennisbank',
      description: 'Self-service portal voor veelgestelde vragen en handleidingen.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden bg-gradient-to-br from-blue-50 via-white to-blue-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-6">
                Helpdesk Software
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Zoho Desk: Uitstekende Klantenservice Begint Hier
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Lever eersteklas klantenservice met Zoho Desk. Beheer alle klantcontacten vanuit één centrale plek 
                en verhoog de klanttevredenheid.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button 
                    size="lg" 
                    className="bg-blue-700 hover:bg-blue-800 text-white px-8 py-6 text-lg font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 group"
                  >
                    Vraag een Demo Aan
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                  </Button>
                </Link>
                
                <a href="tel:0638823471">
                  <Button 
                    size="lg" 
                    variant="outline"
                    className="border-2 border-blue-700 text-blue-700 hover:bg-blue-50 px-8 py-6 text-lg font-semibold rounded-lg transition-all duration-300"
                  >
                    Bel: 06 3882 3471
                  </Button>
                </a>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1608222351212-18fe0ec7b13b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwyfHxkYXNoYm9hcmR8ZW58MHx8fHwxNzczODE3NjA4fDA&ixlib=rb-4.1.0&q=85"
                alt="Zoho Desk Dashboard"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* What is Zoho Desk Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Wat is Zoho Desk?
            </h2>
            <div className="prose prose-lg text-gray-600 space-y-4">
              <p>
                Zoho Desk is een krachtige helpdesk software oplossing die bedrijven helpt om uitstekende 
                klantenservice te leveren. Met Zoho Desk centraliseert u alle klantcontacten - of ze nu binnenkomen 
                via email, telefoon, chat of social media - in één overzichtelijk platform.
              </p>
              <p>
                Als gecertificeerd Zoho Partner implementeert Guijs Consultancy Zoho Desk zo dat het perfect aansluit 
                op uw support processen. Wij zorgen voor een naadloze integratie met uw bestaande systemen zoals 
                Zoho CRM, telefonie en andere tools.
              </p>
              <p>
                Of u nu een klein support team heeft of een grote klantenservice organisatie, Zoho Desk schaalt mee 
                met uw groei en biedt alle functionaliteit die u nodig heeft voor professionele klantenservice.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Krachtige Functies van Zoho Desk
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Alles wat u nodig heeft om excellente klantenservice te leveren.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={`item-${index}`} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardContent className="p-6">
                  <div className="mb-4">{feature.icon}</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.pexels.com/photos/3758159/pexels-photo-3758159.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Customer Support"
                className="rounded-2xl shadow-xl"
              />
            </div>

            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Voordelen van Zoho Desk voor Uw Bedrijf
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Zoho Desk helpt u om sneller, efficiënter en persoonlijker te reageren op klantvragen, 
                wat resulteert in hogere klanttevredenheid en loyaliteit.
              </p>

              <ul className="space-y-4">
                {[
                  'Verhoog responstijd met automatische ticket routing',
                  'Verbeter klanttevredenheid met snellere oplossingen',
                  'Verminder support kosten door efficiëntere processen',
                  'Krijg inzicht in team prestaties met uitgebreide rapportages',
                  'Bied 24/7 support met self-service kennisbank',
                  'Integreer naadloos met Zoho CRM en andere tools',
                ].map((benefit, index) => (
                  <li key={`item-${index}`} className="flex items-start space-x-3">
                    <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-lg">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Waarom Guijs Consultancy voor Zoho Desk?
            </h2>
            <p className="text-xl text-gray-600 mb-12">
              Wij zorgen voor een succesvolle Zoho Desk implementatie die uw klantenservice naar een hoger niveau tilt.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-left">
              <Card className="border-2 border-blue-200 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Expert Configuratie</h3>
                  <p className="text-gray-600">
                    Wij configureren Zoho Desk volgens best practices en uw specifieke support processen.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-blue-200 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Integraties</h3>
                  <p className="text-gray-600">
                    Koppeling met Zoho CRM, telefonie, chat tools en andere systemen voor een compleet overzicht.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-blue-200 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Team Training</h3>
                  <p className="text-gray-600">
                    Uitgebreide training voor uw support team zodat ze direct productief kunnen werken.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-blue-200 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Doorlopende Optimalisatie</h3>
                  <p className="text-gray-600">
                    Continue verbetering van uw helpdesk setup voor maximale efficiency.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection 
        title="Klaar voor Betere Klantenservice?"
        subtitle="Ontdek hoe Zoho Desk uw support team kan helpen om sneller en beter te werken."
      />
    </div>
  );
};

export default ZohoDesk;
